import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { withSize } from 'react-sizeme';

import {
    Sizes,
    Colours,
    Locales,
    IconPlus,
    IconMinus,
    TooltipAnchor,
    CondensedTheme,
    useSmoothCollapse,
    PageBehaviourButton,
    CircleSpinner,
} from '@ratehub/base-ui';
import { GIC_TERMS, GIC_TYPES, GIC_MESSAGES } from '@ratehub/inv-common';

import GICsAccountPropType from '../definitions/GICsAccountPropType';
import ProductTableSponsoredGIC from './ProductTableSponsoredGIC';
import ProductTableBestGICRatesTableCell from './ProductTableBestGICRatesTableCell';


function ProductTableBestGICRatesTable({
    sponsoredGIC,
    rates = [],
    onRateClick,
    isLoading = false,
    className,
    size,
    ...otherProps
}) {
    const intl = useIntl();
    const containerRef = useRef(null);
    const [ isExpanded, toggleIsExpanded ] = useSmoothCollapse({ containerRef });

    /* TODO EVR-2469: This will be configurable via a prop and cleaned up */
    const isExpandable = rates.length > 3;

    // we don’t care how the GIC rates appear within rates[]; this function organizes them as we require
    const displayedRates = getRowsFromRates(rates, isExpandable, isExpanded);

    const isMoreDetailsInLightbox = size?.width < 475;


    return (
        <CondensedTheme>
            <If condition={sponsoredGIC}>
                <ProductTableSponsoredGIC
                    gicAccount={sponsoredGIC}
                    onRateClick={onRateClick}
                    className="rh-mb-1"
                />
            </If>

            <div
                className={classNames('rh-b-1px-solid-stone', className)}
                {...otherProps}
                ref={containerRef}
            >
                <If condition={isLoading}>
                    <CircleSpinner />
                </If>

                <Choose>
                    <When condition={displayedRates?.length > 0}>
                        <table className="best-gic-rates-table rh-width-100p">
                            <thead>
                                <tr className="table-heading">
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <FormattedMessage
                                            id="web-components.best-gic-rates.term-table-header"
                                            defaultMessage="Term"
                                        />
                                    </th>
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <div className="rh-display-flex rh-align-items-center">
                                            <FormattedMessage
                                                id="web-components.best-gic-rates.registered"
                                                defaultMessage="Registered"
                                            />
                                            <TooltipAnchor
                                                className="rh-ml-0_5"
                                                message={(
                                                    <FormattedMessage
                                                        id="web-components.best-gic-rates.registered.tooltip"
                                                        defaultMessage="Registered GICs are held within government-regulated accounts like TFSAs, RRSPs, RESPs, etc."
                                                    />
                                                )}
                                                tooltipLocation="bottom"
                                                nubbinAlignment="middle"
                                                width="20em"
                                            />
                                        </div>
                                    </th>
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <div className="rh-display-flex rh-align-items-center">
                                            <FormattedMessage
                                                id="web-components.best-gic-rates.non-registered"
                                                defaultMessage="Non-registered"
                                            />
                                            <TooltipAnchor
                                                className="rh-ml-0_5"
                                                message={(
                                                    <FormattedMessage
                                                        id="web-components.best-gic-rates.non-registered.tooltip"
                                                        defaultMessage="Unregistered GICs are held within accounts that are not government-regulated and are taxable."
                                                    />
                                                )}
                                                tooltipLocation="bottom"
                                                nubbinAlignment="end"
                                                width="20em"
                                            />
                                        </div>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <For
                                    each="row"
                                    of={displayedRates}
                                    index="index"
                                >
                                    <tr
                                        key={row.term}
                                        className={classNames('table-row', {
                                            'is-hidden': row.isHidden,
                                            'hide-bottom-border': !isExpandable && row === displayedRates[displayedRates.length - 1],
                                        })}
                                        aria-hidden={row.isHidden}
                                    >
                                        <td className="rh-p-1">
                                            <p className="rh-title-2xs weight-medium rh-mt-0 rh-mb-0_5">
                                                <FormattedMessage
                                                    {...GIC_MESSAGES.gicTermNameShort}
                                                    values={{
                                                        term: row.term,
                                                        years: row.term / 12,
                                                    }}
                                                />
                                            </p>
                                            <If condition={TERMS_WITH_COMPARE_LINKS.includes(row.term)}>
                                                <a
                                                    className="rh-anchor-with-icon• rh-anchor-animation rh-text-xs rh-my-0"
                                                    href={getCompareAllRatesLinks(row, intl)}
                                                    data-test-name="bestGICRatesTable-compareAllRates-cta"
                                                >
                                                    <FormattedMessage
                                                        id="web-components.best-gic-rates.compare-all-term-rates"
                                                        defaultMessage={'Compare all rates'}
                                                    />
                                                </a>
                                            </If>
                                        </td>
                                        <td className={RATE_TABLE_CELL_CLASSES}>
                                            <ProductTableBestGICRatesTableCell
                                                rate={row.registered}
                                                onRateClick={onRateClick}
                                                isMoreDetailsInLightbox={isMoreDetailsInLightbox}
                                            />
                                        </td>
                                        <td className={RATE_TABLE_CELL_CLASSES}>
                                            <ProductTableBestGICRatesTableCell
                                                rate={row.nonRegistered}
                                                onRateClick={onRateClick}
                                                isMoreDetailsInLightbox={isMoreDetailsInLightbox}
                                            />
                                        </td>
                                    </tr>
                                </For>
                            </tbody>
                        </table>

                        <If condition={isExpandable}>
                            <PageBehaviourButton
                                className="show-more-button rh-mx-auto"
                                data-name="best-gic-rates-show-more"

                                onClick={toggleIsExpanded}
                                message={(
                                    <FormattedMessage
                                        id="web-components.best-gic-rates.toggle-expanded"
                                        defaultMessage="{ isExpanded, select, true {Show less} other {Show more} }"
                                        values={{ isExpanded }}
                                    />
                                )}

                                size="small"
                                iconLocation="left"
                                iconComponent={isExpanded ? IconMinus : IconPlus}
                            />
                        </If>
                    </When>
                    <Otherwise>
                        <p className="rh-text-m rh-py-5 rh-text-align-center">
                            <FormattedMessage
                                id="web-components.best-gic-rates.no-rates-available"
                                defaultMessage="No rates are available for this search criteria"
                            />
                        </p>
                    </Otherwise>
                </Choose>
            </div>
        </CondensedTheme>
    );
}

ProductTableBestGICRatesTable.propTypes = {
    sponsoredGIC: GICsAccountPropType,
    rates: PropTypes.arrayOf(GICsAccountPropType),
    onRateClick: PropTypes.func.isRequired,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    size: PropTypes.shape({
        width: PropTypes.number,
    }).isRequired,
};


const TABLE_HEADER_CLASSES = 'table-header-cell rh-text-s weight-regular rh-fg-stone-darkest rh-text-align-left rh-px-0_75 rh-py-0_25';
const RATE_TABLE_CELL_CLASSES = 'table-cell-rate table-cell-with-border rh-position-relative rh-px-0_75 rh-py-1';

const INITIAL_VISIBLE_TERMS = [ 12, 60 ];       // show only 1-year and 5-years to start
const ALWAYS_HIDDEN_TERMS = [];                 // still in the DOM for SEO, but hidden initially
const TERMS_WITH_COMPARE_LINKS = [ 12, 60 ];


/**
 * Organize rows from a collection of GICsAccountPropTypes.
 * The return result will have grouped them into an array of rows with two accounts per row that share the same term.
 * We assume only Registered and Non-registered account types are in the incoming collection of GIC accounts.
 * We are currently accepting null for when the API could not return a rate.
 *
 * @param {GICsAccountPropType[]} rates
 * @param {number} rates.term
 * @param {boolean} isExpandable
 * @param {boolean} isExpanded
 * @return {{term: number, registered: GICsAccountPropType, nonRegistered: GICsAccountPropType, isHidden: boolean}[]}
 */
function getRowsFromRates(rates, isExpandable, isExpanded) {
    const nonEmptyRates = rates.filter(rate => rate != null);   // remove null or undefined
    const registered = nonEmptyRates.filter(rate => rate.type === GIC_TYPES.REGISTERED);
    const nonRegistered = nonEmptyRates.filter(rate => rate.type === GIC_TYPES.NONREGISTERED);

    const rateRows = GIC_TERMS.map(term => ({
        term: term,
        registered: registered
            .filter(rate => rate.term === term)?.[0],

        nonRegistered: nonRegistered
            .filter(rate => rate.term === term)?.[0],

        isHidden: isRowHidden(term, isExpandable, isExpanded),
    }));

    // skip term row that has neither registered nor nonRegistered rates
    return rateRows.filter(row => row.registered || row.nonRegistered);
}

function isRowHidden(term, isExpandable, isExpanded) {
    if (ALWAYS_HIDDEN_TERMS.includes(term)) {
        return true;
    }

    // If we’re not expandable, all rows should be visible
    if (!isExpandable) {
        return false;
    }

    // Hidden when the table is not expanded, and we’re not in the default visible terms
    return !isExpanded && !INITIAL_VISIBLE_TERMS.includes(term);
}

function getCompareAllRatesLinks(row, intl) {
    // If term is rendered without either registered or nonRegistered rates, don’t show the link
    if (!(row?.registered?.type || row?.nonRegistered?.type)) {
        return null;
    }

    // only supporting 1- and 5-year at the moment
    if (row.term === 12 || row.term === 60) {
        const years = row.term / 12;

        // picking 'non-registered' as destination link
        return intl.locale === Locales.FRENCH
            ? `/cpg/taux-cpg/non-enregistre/${years}-ans`
            : `/gics/gic-rates/non-registered/${years}-year`;
    }

    return null;
}


const StyledProductTableBestGICRatesTable = styled(ProductTableBestGICRatesTable)`
    background-color: ${Colours.COCONUT};

    > .best-gic-rates-table {
        border-collapse: collapse;

        .table-heading,
        .table-row:not(.hide-bottom-border) {
            border-bottom: 1px solid ${Colours.STONE};
        }

        .table-row {
            vertical-align: top;

            &.is-hidden {
                display: none;
            }

            > .table-cell-term {
                width: 15%;
            }

            > .table-cell-rate {
                width: 42.5%;
            }

            > .table-cell-with-border {
                /* Using a pseudo element as a left border since the designs have white space above
                    and below the border, which is not possible with border-left properties. */
                &::before {
                    position: absolute;
                    top: ${Sizes.SPACING.ONE};
                    bottom: ${Sizes.SPACING.ONE};
                    left: 0;

                    content: '';

                    width: 1px;
                    background-color: ${Colours.STONE};
                }
            }
        }
    }

    > .show-more-button {
        /* require padding here to override styles within PageBehaviourButton */
        padding-top:    ${Sizes.SPACING.ONE_AND_A_QUARTER};
        padding-bottom: ${Sizes.SPACING.ONE_AND_A_QUARTER};
    }
`;


export default withSize({ noPlaceholder: true })(StyledProductTableBestGICRatesTable);

